import React, { useState, useEffect } from 'react';
import './CommonStyles.css';
import './SendNotificationModal.css';
import apiService from '../../services/api';

const PurchaseNotificationModal = ({ purchase, onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('email');
  const [emailData, setEmailData] = useState({
    to: '',
    subject: '',
    body: ''
  });
  const [smsData, setSmsData] = useState({
    to: '',
    message: ''
  });
  const [isSending, setIsSending] = useState(false);
  const [businessName, setBusinessName] = useState('Our Business');

  useEffect(() => {
    const fetchBiz = async () => {
      try {
        const res = await apiService.getBusinessDetails();
        const name = res?.data?.name || res?.name || 'Our Business';
        setBusinessName(name);
      } catch (e) {}
    };
    fetchBiz();
  }, []);

  useEffect(() => {
    if (purchase) {
      const apiData = purchase.apiData || purchase;
      const vendorName = apiData.supplier?.name || apiData.contact?.name || 'Vendor';
      const vendorEmail = apiData.supplier?.email || apiData.contact?.email || '';
      const vendorPhone = apiData.supplier?.mobile || apiData.contact?.mobile || '';
      const refNo = apiData.ref_no || '';
      
      const prefilledBody = `Dear ${vendorName},\n\nThis is a notification regarding purchase order ${refNo}. Some items are still pending from this order.\n\nPlease provide an update on the expected delivery date.\n\nThank you,\n${businessName}`;
      
      const prefilledSms = `Hello ${vendorName}, updating you on PO ${refNo}. Items are still pending. Please update us on delivery status. Thanks, ${businessName}`;

      setEmailData({
        to: vendorEmail,
        subject: `Pending Items Notification - ${refNo}`,
        body: prefilledBody
      });
      
      setSmsData({
        to: vendorPhone,
        message: prefilledSms
      });
    }
  }, [purchase, businessName]);

  const availableTags = [
    '{business_name}', '{ref_no}', '{vendor_name}', '{total_amount}', '{paid_amount}', '{due_amount}'
  ];

  const insertTag = (tag) => {
    if (activeTab === 'email') {
      setEmailData(prev => ({ ...prev, body: prev.body + tag }));
    } else {
      setSmsData(prev => ({ ...prev, message: prev.message + tag }));
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setIsSending(true);
    try {
      await apiService.sendPurchaseNotification({
        purchase_id: purchase.id,
        type: 'email',
        to: emailData.to,
        subject: emailData.subject,
        body: emailData.body
      });
      alert('Email sent successfully!');
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email: ' + error.message);
    } finally {
      setIsSending(false);
    }
  };

  const handleSendSMS = async (e) => {
    e.preventDefault();
    setIsSending(true);
    try {
      await apiService.sendPurchaseNotification({
        purchase_id: purchase.id,
        type: 'sms',
        to: smsData.to,
        message: smsData.message
      });
      alert('Notification sent successfully!');
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Error sending SMS:', error);
      alert('Failed to send notification: ' + error.message);
    } finally {
      setIsSending(false);
    }
  };

  if (!purchase) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container notification-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '95%' }}>
        <div className="modal-header">
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#334155' }}>Items Pending Notification</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content" style={{ padding: '1.5rem', overflowY: 'auto', maxHeight: '80vh' }}>
          <div className="available-tags-section" style={{ marginBottom: '1.25rem', width: '100%', background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <h3 style={{ fontSize: '0.85rem', marginBottom: '0.75rem', color: '#64748b', fontWeight: '600' }}>Available Tags:</h3>
            <div className="tags-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', width: '100%' }}>
              {availableTags.map((tag, index) => (
                <button key={index} type="button" className="tag-btn" onClick={() => insertTag(tag)} style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '4px', color: '#4f46e5', cursor: 'pointer' }}>
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <div className="notification-tabs" style={{ marginBottom: '1.25rem', width: '100%', display: 'flex', borderBottom: '1px solid #e2e8f0' }}>
            <button className={`tab-btn ${activeTab === 'email' ? 'active' : ''}`} onClick={() => setActiveTab('email')} style={{ flex: 1, padding: '0.75rem', background: 'none', border: 'none', borderBottom: activeTab === 'email' ? '2px solid #4f46e5' : 'none', color: activeTab === 'email' ? '#4f46e5' : '#64748b', fontWeight: '600', cursor: 'pointer' }}>📧 Email</button>
            <button className={`tab-btn ${activeTab === 'sms' ? 'active' : ''}`} onClick={() => setActiveTab('sms')} style={{ flex: 1, padding: '0.75rem', background: 'none', border: 'none', borderBottom: activeTab === 'sms' ? '2px solid #4f46e5' : 'none', color: activeTab === 'sms' ? '#4f46e5' : '#64748b', fontWeight: '600', cursor: 'pointer' }}>📱 SMS/WhatsApp</button>
          </div>

          {activeTab === 'email' ? (
            <form onSubmit={handleSendEmail} style={{ width: '100%' }}>
              <div className="form-group" style={{ marginBottom: '1rem', width: '100%' }}>
                <label style={{ fontWeight: '600', fontSize: '0.875rem', display: 'block', marginBottom: '0.4rem' }}>To:*</label>
                <input type="email" value={emailData.to} onChange={(e) => setEmailData({...emailData, to: e.target.value})} required style={{ width: '100%', padding: '0.6rem', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none' }} />
              </div>
              <div className="form-group" style={{ marginBottom: '1rem', width: '100%' }}>
                <label style={{ fontWeight: '600', fontSize: '0.875rem', display: 'block', marginBottom: '0.4rem' }}>Subject:*</label>
                <input type="text" value={emailData.subject} onChange={(e) => setEmailData({...emailData, subject: e.target.value})} required style={{ width: '100%', padding: '0.6rem', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none' }} />
              </div>
              <div className="form-group" style={{ marginBottom: '0.5rem', width: '100%' }}>
                <label style={{ fontWeight: '600', fontSize: '0.875rem', display: 'block', marginBottom: '0.4rem' }}>Body:*</label>
                <textarea value={emailData.body} onChange={(e) => setEmailData({...emailData, body: e.target.value})} rows="6" required style={{ width: '100%', padding: '0.6rem', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', resize: 'vertical' }} />
              </div>
              <div className="modal-footer" style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '1rem',
                padding: '1.25rem 0 0.5rem 0',
                marginTop: '1.5rem',
                borderTop: '1px solid #f1f5f9'
              }}>
                <button type="submit" className="modal-btn" disabled={isSending} style={{ backgroundColor: '#4f46e5', color: 'white', padding: '0.65rem 2rem', borderRadius: '6px', border: 'none', fontWeight: '700', cursor: isSending ? 'not-allowed' : 'pointer' }}>
                  {isSending ? 'Sending...' : 'Send Email'}
                </button>
                <button type="button" className="modal-btn" onClick={onClose} style={{ backgroundColor: '#334155', color: 'white', padding: '0.65rem 2rem', borderRadius: '6px', border: 'none', fontWeight: '700', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSendSMS} style={{ width: '100%' }}>
              <div className="form-group" style={{ marginBottom: '1rem', width: '100%' }}>
                <label style={{ fontWeight: '600', fontSize: '0.875rem', display: 'block', marginBottom: '0.4rem' }}>To (Phone):*</label>
                <input type="tel" value={smsData.to} onChange={(e) => setSmsData({...smsData, to: e.target.value})} required style={{ width: '100%', padding: '0.6rem', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none' }} />
              </div>
              <div className="form-group" style={{ marginBottom: '0.5rem', width: '100%' }}>
                <label style={{ fontWeight: '600', fontSize: '0.875rem', display: 'block', marginBottom: '0.4rem' }}>Message:*</label>
                <textarea value={smsData.message} onChange={(e) => setSmsData({...smsData, message: e.target.value})} rows="5" required style={{ width: '100%', padding: '0.6rem', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', resize: 'vertical' }} />
              </div>
              <div className="modal-footer" style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '1rem',
                padding: '1.25rem 0 0.5rem 0',
                marginTop: '1.5rem',
                borderTop: '1px solid #f1f5f9'
              }}>
                <button type="submit" className="modal-btn" disabled={isSending} style={{ backgroundColor: '#4f46e5', color: 'white', padding: '0.65rem 2rem', borderRadius: '6px', border: 'none', fontWeight: '700', cursor: isSending ? 'not-allowed' : 'pointer' }}>
                  {isSending ? 'Sending...' : 'Send Notification'}
                </button>
                <button type="button" className="modal-btn" onClick={onClose} style={{ backgroundColor: '#334155', color: 'white', padding: '0.65rem 2rem', borderRadius: '6px', border: 'none', fontWeight: '700', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseNotificationModal;
